import { useState, useEffect, useRef, type FormEvent } from 'react'
import { useAuth } from '../contexts/AuthContext'
import './Login.css'

interface LoginResponse {
  success: boolean
  message?: string
  user?: {
    id: number
    username: string
    role: string
    must_change_password: boolean
  }
  locked_out?: boolean
  lockout_seconds_left?: number
}

export function Login() {
  const { setUser } = useAuth()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const [lockoutSecondsLeft, setLockoutSecondsLeft] = useState<number | null>(null)
  const lockoutIntervalRef = useRef<number | null>(null)

  // Countdown timer for lockout
  useEffect(() => {
    if (lockoutSecondsLeft !== null && lockoutSecondsLeft > 0) {
      lockoutIntervalRef.current = window.setInterval(() => {
        setLockoutSecondsLeft(prev => {
          if (prev === null || prev <= 1) {
            if (lockoutIntervalRef.current) {
              clearInterval(lockoutIntervalRef.current)
              lockoutIntervalRef.current = null
            }
            return null
          }
          return prev - 1
        })
      }, 1000)
    }

    return () => {
      if (lockoutIntervalRef.current) {
        clearInterval(lockoutIntervalRef.current)
      }
    }
  }, [lockoutSecondsLeft])

  const formatLockoutTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60)
    const secs = seconds % 60
    if (minutes > 0) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''} and ${secs} second${secs !== 1 ? 's' : ''}`
    }
    return `${secs} second${secs !== 1 ? 's' : ''}`
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username,
          password,
          remember_me: rememberMe,
        }),
      })

      const data: LoginResponse = await response.json()

      if (data.success && data.user) {
        setLockoutSecondsLeft(null)
        // Update auth context with logged-in user
        setUser({
          id: data.user.id,
          username: data.user.username,
          role: data.user.role,
          must_change_password: data.user.must_change_password
        })
        // Navigation will be handled by App.tsx redirect logic
      } else if (!data.success) {
        if (data.locked_out && data.lockout_seconds_left) {
          setLockoutSecondsLeft(data.lockout_seconds_left)
        }
        setError(data.message || 'Login failed')
      }
    } catch {
      setError('Could not connect to server')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-card card">
          <h1 className="login-title">Log in</h1>

          <form onSubmit={handleSubmit} className="login-form">
            {lockoutSecondsLeft !== null && lockoutSecondsLeft > 0 && (
              <div className="login-lockout">
                <span className="lockout-icon">🔒</span>
                <div className="lockout-message">
                  <strong>Account locked</strong>
                  <p>Too many failed login attempts.</p>
                  <p>Try again in: <strong>{formatLockoutTime(lockoutSecondsLeft)}</strong></p>
                </div>
              </div>
            )}

            {error && !lockoutSecondsLeft && (
              <div className="login-error">
                {error}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="username">Username</label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username"
                required
                autoComplete="username"
                autoFocus
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                required
                autoComplete="current-password"
              />
            </div>

            <div className="form-group form-group-checkbox">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                Remember me
              </label>
            </div>

            <button
              type="submit"
              className="btn btn-primary login-button"
              disabled={loading || (lockoutSecondsLeft !== null && lockoutSecondsLeft > 0)}
            >
              {loading ? 'Logging in...' : 'Log in'}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
